import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * useTextClamp hook detects if text content is being clamped (truncated) due to CSS constraints.
 *
 * Features:
 * - Detects if text content is overflowing its container
 * - Responds to resize events automatically
 * - Supports conditional/async rendering through callback ref
 * - Can be disabled by not setting the ref
 *
 * @returns {Object} - Object containing:
 *   - ref: Callback ref to attach to the text container element
 *   - isClamped: Boolean indicating if the text is currently clamped/truncated
 */
export const useTextClamp = () => {
  const elementRef = useRef<HTMLElement>(null);
  const animationFrameId = useRef<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [isClamped, setIsClamped] = useState(false);

  const checkClamping = () => {
    if (animationFrameId.current !== null) {
      cancelAnimationFrame(animationFrameId.current);
    }

    animationFrameId.current = requestAnimationFrame(() => {
      const element = elementRef.current;
      if (!element) {
        return;
      }

      setIsClamped(element.scrollHeight > element.clientHeight);
    });
  };

  const cleanupResources = () => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  };

  const setupObserver = () => {
    cleanupResources();

    const element = elementRef.current;
    if (!element) {
      setIsClamped(false);
      return;
    }

    const observer = new ResizeObserver(checkClamping);
    observer.observe(element);
    observerRef.current = observer;

    checkClamping();
  };

  const ref = useCallback((element: HTMLElement | null) => {
    elementRef.current = element;
    setupObserver();
  }, []);

  useEffect(() => {
    return cleanupResources;
  }, []);

  return {
    ref,
    isClamped,
  };
};
